import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { Alert, Button, TextInput } from "@/ui";
import { AuthCard, AuthLayout } from "@/layout/AuthLayout";
import { useTenant } from "@/tenant/TenantProvider";
import { api, ApiClientError } from "@/lib/api";
import { loginIdFields } from "@gatiq/shared";

/**
 * Password recovery entry point for a locked-out staff member (security
 * audit item 5). Handles both the platform (`/forgot-password`, no
 * `orgSlug`) and an organization's own staff sign-in
 * (`/:orgSlug/forgot-password`) — same structure as `OrgStaffLogin`.
 *
 * The API always answers 204 here, whether or not the email is registered,
 * so this screen must never let a visitor learn which emails exist: the
 * "sent" confirmation is shown unconditionally on submit, success or not.
 */
export function ForgotPassword() {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { payload, error: tenantError, loading: tenantLoading } = useTenant();

  const [email, setEmail] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFields({});
    setSubmitting(true);
    try {
      const path = orgSlug ? `/auth/${orgSlug}/forgot-password` : "/auth/forgot-password";
      // Staff may have no email (D-18); an org account can be found by mobile too.
      await api.post(path, orgSlug ? loginIdFields(email) : { email });
      setSent(true);
    } catch (err) {
      // Only a validation problem with the email field itself is worth
      // showing — anything else (network hiccup, rate limit) still resolves
      // into the same confirmation, never a hint about server-side state.
      if (err instanceof ApiClientError && err.fields) {
        setFields(err.fields);
      } else {
        setSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const brand = payload.branding;
  const orgName = payload.orgName ?? brand.appName ?? "";
  const loginPath = orgSlug ? `/${orgSlug}/login` : "/login";

  return (
    <AuthLayout
      accent="right back in."
      blurb={
        orgSlug
          ? `We'll send a reset link for your ${orgName} account.`
          : "We'll email a reset link for your platform account."
      }
      headline="Get you"
      points={[
        "The link works once and expires in 60 minutes",
        "It goes only to your own email or WhatsApp",
        "You'll choose a brand-new password",
      ]}
    >
      <AuthCard
        footer={
          <>
            Remembered it?{" "}
            <Link className="font-medium text-brand-ink underline" to={loginPath}>
              Back to sign in
            </Link>
          </>
        }
        subtitle="We'll send you a link to reset it"
        title="Forgot your password?"
      >
        {tenantError && !tenantLoading ? (
          <Alert kind="danger">{tenantError}</Alert>
        ) : sent ? (
          <Alert kind="success">
            If an account exists for that {orgSlug ? "email or mobile number" : "email"}, we&rsquo;ve sent a
            link to reset the password. It expires in 60 minutes.
          </Alert>
        ) : (
          <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
            <TextInput
              autoComplete={orgSlug ? "username" : "email"}
              autoFocus
              error={fields.email ?? fields.phone}
              label={orgSlug ? "Email or mobile number" : "Email"}
              onChange={(e) => setEmail(e.target.value)}
              required
              type={orgSlug ? "text" : "email"}
              value={email}
            />
            <Button block loading={submitting} type="submit">
              Send reset link
            </Button>
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
